import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import moment from 'moment';
import {
  GET_HOME_OVERVIEW_DATA,
  GET_USERS_CHART_DATA,
  GET_USERS_DAILY_SIGNUP_CHART_DATA,
} from 'graphql/queries/users';
import {RecentlyJoinedUsers} from './users/RecentlyJoinedUsers';
import {SuperAdminOverview} from './SuperAdminOverview/SuperAdminOverview';
import {Section, StatCard, StatRow} from './shared/DashboardUI';
import GrowthChart from './shared/GrowthChart';
import TrendChart from './shared/TrendChart';
import MonthNavigator from './shared/MonthNavigator';
import {plainNumber} from './shared/chartTheme';
import styles from './shared/dashboard.module.scss';

/**
 * The dashboard landing page.
 *
 * Both views now draw from the same shared primitives, so switching between
 * them is a change of depth rather than a change of product.
 */

const AdminDashboardHome = () => {
  const [currentView, setCurrentView] = useState('super_admin');
  const userRole = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab?.name,
  );

  const handleSwitch = () =>
    setCurrentView(currentView === 'super_admin' ? 'default' : 'super_admin');

  // Was wrapped in useMemo keyed on [userRole, currentView], which memoised an
  // element that is cheap to create and went stale against `handleSwitch`.
  if (userRole !== 'SUPERADMIN' || currentView === 'default')
    return <DefaultOverview handleSwitch={handleSwitch} userRole={userRole} />;
  return <SuperAdminOverview handleSwitch={handleSwitch} />;
};

/**
 * Granularity. The old chart offered the same four, but only DAY was wired to
 * a month; WEEK, MONTH and YEAR silently showed the entire history with no
 * indication of the range they covered.
 */
const GRAINS = [
  {value: 'DAY', label: 'Daily'},
  {value: 'WEEK', label: 'Weekly'},
  {value: 'MONTH', label: 'Monthly'},
  {value: 'YEAR', label: 'Yearly'},
];

const GRAIN_UNIT = {WEEK: 'week', MONTH: 'month', YEAR: 'year'};

const toPoints = daily =>
  (daily ?? []).map(item => ({date: item?.createdAt, amount: item?.total}));

const DefaultOverview = ({handleSwitch, userRole}) => {
  const [grain, setGrain] = useState('DAY');
  const [month, setMonth] = useState(() => moment());
  const isDaily = grain === 'DAY';
  const previous = useMemo(() => month.clone().subtract(1, 'month'), [month]);

  const {data, loading} = useQuery(GET_HOME_OVERVIEW_DATA, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const {data: chartData, loading: chartLoading} = useQuery(
    GET_USERS_DAILY_SIGNUP_CHART_DATA,
    {
      variables: {month: month.month() + 1, year: month.year()},
      skip: !isDaily,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      onError: () => toast.error('Could not load the signups chart'),
    },
  );

  const {data: trendData, loading: trendLoading} = useQuery(
    GET_USERS_CHART_DATA,
    {
      skip: isDaily,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      onError: () => toast.error('Could not load the signup trend'),
    },
  );

  // Fetched alongside the current month purely so the summary can say whether
  // this month is ahead of the last one.
  const {data: previousChartData, loading: previousLoading} = useQuery(
    GET_USERS_DAILY_SIGNUP_CHART_DATA,
    {
      variables: {month: previous.month() + 1, year: previous.year()},
      skip: !isDaily,
      errorPolicy: 'all',
    },
  );

  const statistics = data?.getAllUsers?.statistics;
  const daily = chartData?.getUsersDailySignupChartData?.daily;
  const previousDaily = previousChartData?.getUsersDailySignupChartData?.daily;

  const trendSeries = isDaily
    ? null
    : toPoints(
        trendData?.getUsersSignupChartData?.[
          grain === 'WEEK' ? 'weekly' : grain === 'MONTH' ? 'monthly' : 'yearly'
        ],
      );

  const grainSwitch = (
    <div className={styles.segmented} role="group" aria-label="Granularity">
      {GRAINS.map(item => (
        <button
          key={item.value}
          type="button"
          className={`${styles.segment} ${
            grain === item.value ? styles.segmentActive : ''
          }`}
          onClick={() => setGrain(item.value)}>
          {item.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <div>
          <h1>Overview</h1>
          <p>How the platform is doing today</p>
        </div>
        {userRole === 'SUPERADMIN' && (
          <button
            type="button"
            className={styles.switchButton}
            onClick={handleSwitch}>
            Switch to super admin dashboard
          </button>
        )}
      </header>

      <StatRow columns={3}>
        <StatCard
          label="Total users"
          value={plainNumber(statistics?.totalUsers)}
          hint="registered accounts"
          loading={loading}
        />
        <StatCard
          label="Companies"
          value={plainNumber(data?.getTotalNumberOfCompanies)}
          hint="listed on the platform"
          loading={loading}
        />
        <StatCard
          label="Active investors"
          value={plainNumber(statistics?.totalActiveInvestors)}
          hint="with settled orders"
          loading={loading}
        />
      </StatRow>

      <Section
        title="Signups"
        description={
          isDaily
            ? `New accounts on each day of ${month.format(
                'MMMM YYYY',
              )}. Days with none are shown as zero.`
            : 'New accounts across the whole record. Quiet periods are shown as zero.'
        }
        actions={
          <div className={styles.growthControls}>
            {grainSwitch}
            {isDaily && (
              <MonthNavigator
                value={month}
                onChange={setMonth}
                disabled={chartLoading}
              />
            )}
          </div>
        }>
        {isDaily ? (
          <GrowthChart
            points={toPoints(daily)}
            month={month.month() + 1}
            year={month.year()}
            label="Users"
            previousPoints={previousDaily ? toPoints(previousDaily) : null}
            previousLoading={previousLoading}
            loading={chartLoading}
          />
        ) : (
          <TrendChart
            points={trendSeries}
            unit={GRAIN_UNIT[grain]}
            loading={trendLoading}
          />
        )}
      </Section>

      <RecentlyJoinedUsers />
    </div>
  );
};

export {AdminDashboardHome};
